import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, Play, Star } from "lucide-react";
import AnimatedSoundWave from "@/components/landing/AnimatedSoundWave";

const WAVE_PNG =
  "https://static.prod-images.emergentagent.com/jobs/7cad0760-221d-406a-b12b-636e3d27f407/images/d71e23ddc5b76fce17904e35da7313fc2bb452655ba456f6b3015d9ea1d21e46.png";
const STUDIO_PHOTO =
  "https://customer-assets.emergentagent.com/job_cinematic-pods-1/artifacts/9f9cjx1t_Studio%20%20web%20photo%20copy.jpg";
const HERO_VIDEO = `${process.env.REACT_APP_BACKEND_URL}/api/static/videos/hero_reel.mp4`;
const GOOGLE_REVIEW_URL = "https://g.page/r/CepB5VHvhgkqEAE/review";

const STATS = [
  { v: "15+", l: "Years experience" },
  { v: "500+", l: "Projects completed" },
  { v: "3-CAM", l: "4K cinematic setup" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const yMic = useTransform(scrollYProgress, [0, 1], [0, -180]);
  const yWave = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const rotMic = useTransform(scrollYProgress, [0, 1], [0, 12]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] pt-24 md:pt-28 overflow-hidden"
    >
      {/* aurora bg orbs */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-[10%] left-[55%] w-[680px] h-[680px] rounded-full bg-[#6b21d9]/30 blur-[140px]" />
        <div className="absolute top-[40%] left-[10%] w-[420px] h-[420px] rounded-full bg-[#00f0ff]/15 blur-[120px]" />
        <div className="absolute -bottom-20 right-[10%] w-[480px] h-[480px] rounded-full bg-[#ff0055]/15 blur-[140px]" />
      </div>
      <div className="absolute inset-0 -z-10 scanline opacity-30" />

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 relative">
        {/* Floating animated sound wave centerpiece */}
        <motion.div
          style={{ y: yMic, rotate: rotMic, opacity: fade }}
          className="hidden lg:block absolute right-[-40px] xl:right-[-20px] top-10 w-[340px] xl:w-[460px] pointer-events-none select-none z-10"
          data-testid="hero-floating-wave-visual"
        >
          <AnimatedSoundWave className="w-full" data-testid="hero-soundwave" />
        </motion.div>
        <motion.img
          src={WAVE_PNG}
          alt=""
          aria-hidden
          loading="lazy"
          decoding="async"
          width={380}
          height={380}
          style={{ y: yWave, opacity: fade }}
          className="hidden lg:block absolute left-[-60px] bottom-10 w-[280px] xl:w-[380px] pointer-events-none select-none mix-blend-screen opacity-80"
          data-testid="hero-floating-wave"
        />

        {/* eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-md"
          data-testid="hero-eyebrow"
        >
          <span className="relative flex w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-[#00f0ff] pulse-ring" />
            <span className="relative w-2 h-2 rounded-full bg-[#00f0ff]" />
          </span>
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#a395c3]">
            Now booking · Lakeland, FL
          </span>
        </motion.div>

        {/* Headline */}
        <div className="mt-7 max-w-4xl">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-black tracking-tighter leading-[0.92] text-[44px] sm:text-[60px] md:text-[72px] lg:text-[100px] xl:text-[124px]"
            data-testid="hero-headline"
          >
            <span className="block text-white">Your voice,</span>
            <span className="block">
              <span className="italic font-light text-[#a395c3]">on </span>
              <span className="relative text-[#00f0ff] text-glow-cyan inline-flex items-baseline">
                br
                <svg
                  viewBox="0 0 100 100"
                  className="mx-[-0.02em]"
                  style={{
                    width: "0.68em",
                    height: "0.68em",
                    transform: "translateY(0.06em)",
                  }}
                  aria-label="podcast microphone"
                  role="img"
                >
                  {/* solid cyan circle */}
                  <circle cx="50" cy="50" r="50" fill="currentColor" />
                  {/* mic silhouette cut out in page bg color */}
                  {/* capsule (mic head + body, rounded rect) */}
                  <rect
                    x="38"
                    y="22"
                    width="24"
                    height="44"
                    rx="12"
                    ry="12"
                    fill="#05050a"
                  />
                  {/* U-shaped yoke around the capsule */}
                  <path
                    d="M28 46 v8 a22 22 0 0 0 44 0 v-8 h-6 v8 a16 16 0 0 1 -32 0 v-8 z"
                    fill="#05050a"
                  />
                  {/* stand stem */}
                  <rect x="47" y="72" width="6" height="8" fill="#05050a" />
                  {/* base */}
                  <rect
                    x="34"
                    y="78"
                    width="32"
                    height="6"
                    rx="2"
                    fill="#05050a"
                  />
                </svg>
                adcast
              </span>
            </span>
            <span className="block">grade.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.8 }}
            className="mt-7 max-w-xl text-[#a395c3] text-base md:text-lg leading-relaxed"
            data-testid="hero-subhead"
          >
            Lakeland&apos;s premium podcast and content studio—built for
            creators and brands who treat every production like a masterpiece.
            Three Sony 4K cameras, 32-bit float audio, and full post-production
            inside a cinematic space designed to make your story land. Walk in
            with an idea—walk out with high-fidelity content that defines your
            brand.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              type="button"
              onClick={() => {
                window.dispatchEvent(new CustomEvent("sb:open-booking"));
              }}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 pl-6 pr-3 py-3 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold hover:bg-white transition-colors cursor-pointer touch-manipulation"
            >
              Book your session
              <span className="w-9 h-9 rounded-full bg-[#05050a] text-[#00f0ff] flex items-center justify-center group-hover:rotate-[-45deg] transition-transform">
                <ArrowDown size={16} className="-rotate-90" />
              </span>
            </button>
            <a
              href="#spotlight"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-3 px-5 py-3 rounded-full border border-white/15 bg-white/[0.02] hover:bg-white/[0.06] transition-colors text-sm text-white/90"
            >
              <Play
                size={14}
                className="fill-[#00f0ff] text-[#00f0ff]"
              />
              Watch the spotlight
            </a>

            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-google-reviews-badge"
              className="inline-flex items-center gap-2 pl-2 pr-4 py-2 rounded-full bg-white/[0.03] border border-white/10 hover:border-[#ffb800]/50 hover:bg-white/[0.06] transition-colors group"
            >
              <div className="flex -space-x-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    size={12}
                    className="fill-[#ffb800] text-[#ffb800]"
                  />
                ))}
              </div>
              <span className="text-[11px] text-white/70 font-medium ml-1 group-hover:text-white transition-colors">
                5.0 · Google Reviews
              </span>
            </a>
          </motion.div>
        </div>

        {/* Studio photo strip + EQ visualizer */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 1 }}
          className="mt-16 md:mt-20 lg:mt-24 grid grid-cols-12 gap-4"
        >
          <div className="col-span-12 lg:col-span-8 relative">
            <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-[16/10] lg:aspect-[16/8]">
              <video
                src={HERO_VIDEO}
                poster={STUDIO_PHOTO}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="hero-studio-video"
              />
              {/* Gradient overlay only on desktop (keeps mobile image clean) */}
              <div className="hidden lg:block absolute inset-0 bg-gradient-to-t from-[#05050a] via-[#05050a]/30 to-transparent" />
              <div className="absolute top-5 left-5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#ff0055] animate-pulse" />
                <span className="text-[10px] uppercase tracking-[0.28em] text-white/80">
                  Live · On-air
                </span>
              </div>
              {/* Caption overlay only on desktop */}
              <div className="hidden lg:flex absolute bottom-5 left-5 right-5 items-end justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#00f0ff]">
                    Recording stage A
                  </div>
                  <div className="font-display font-bold text-2xl mt-1">
                    Multi-cam set up · 32-bit Float Audio · Live Production
                  </div>
                </div>
                <div className="flex items-end gap-[3px] h-10">
                  {Array.from({ length: 14 }).map((_, i) => (
                    <span
                      key={i}
                      className="eq-bar w-[3px] bg-gradient-to-t from-[#00f0ff] to-[#ff0055] rounded-full"
                      style={{
                        animationDelay: `${i * 90}ms`,
                        height: `${30 + ((i * 7) % 70)}%`,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
            {/* Mobile/tablet caption block — sits below image so text is fully readable */}
            <div
              className="lg:hidden mt-4 rounded-3xl bg-white/[0.03] border border-white/10 p-5"
              data-testid="hero-studio-caption-mobile"
            >
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#00f0ff]">
                Recording stage A
              </div>
              <div className="font-display font-bold text-xl mt-1.5 leading-tight">
                Multi-cam set up · 32-bit Float Audio · Live Production
              </div>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-4">
            {STATS.map((s, i) => (
              <motion.div
                key={s.l}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + i * 0.08 }}
                className="relative rounded-2xl p-5 bg-white/[0.03] border border-white/10 backdrop-blur-md overflow-hidden"
                data-testid={`hero-stat-${i}`}
              >
                <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-[#00f0ff]/[0.04] blur-2xl" />
                <div className="font-display font-black text-3xl md:text-4xl tracking-tight">
                  {s.v}
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#a395c3] mt-2">
                  {s.l}
                </div>
              </motion.div>
            ))}

            <motion.a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 + STATS.length * 0.08 }}
              data-testid="hero-google-review-cta"
              className="relative rounded-2xl p-5 bg-gradient-to-br from-[#ffb800]/15 via-white/[0.04] to-[#00f0ff]/10 border border-[#ffb800]/30 backdrop-blur-md overflow-hidden group hover:from-[#ffb800]/25 hover:border-[#ffb800]/50 transition-all"
            >
              <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-[#ffb800]/[0.18] blur-2xl group-hover:bg-[#ffb800]/[0.32] transition-all" />
              <div className="relative flex -space-x-1 mb-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    size={13}
                    className="fill-[#ffb800] text-[#ffb800]"
                  />
                ))}
              </div>
              <div className="relative font-display font-black text-base md:text-lg tracking-tight leading-tight">
                5 Star Google Rated
              </div>
              <div className="relative text-[10px] uppercase tracking-[0.22em] text-[#a395c3] mt-2 inline-flex items-center gap-1.5 group-hover:text-white transition-colors">
                Tap to see reviews
                <ArrowDown
                  size={10}
                  className="rotate-[-135deg] group-hover:rotate-[-90deg] transition-transform"
                />
              </div>
            </motion.a>
          </div>
        </motion.div>

        {/* scroll cue */}
        <div className="hidden md:flex items-center gap-2 mt-12 text-[10px] uppercase tracking-[0.3em] text-white/40">
          <ArrowDown size={12} className="animate-bounce" /> Scroll to explore
        </div>
      </div>
    </section>
  );
};

export default Hero;
